import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Form, Input, Space, Typography } from 'antd';
import { useUser } from '../../contexts/UserContext';
import { createDeferredSubscription } from '../../services/api/billingService';
import { SubscriptionService } from '../../services/api/subscriptionService';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { resolveWorkspaceCompanyId } from '../../utils/resolveWorkspaceCompanyId';

const { Title, Paragraph } = Typography;

const DEFAULT_CODE = 'PARTNER_DEFERRED_CORE';

/**
 * Partner_bundle enrollment: POST /billing/deferred-subscription (no Stripe redirect).
 */
export const PartnerDeferredEnrollmentPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, selectedCompany, availableCompanies: companiesList = [] } = useUser();
  const companyId = useMemo(
    () => resolveWorkspaceCompanyId(user?.companyId, selectedCompany?.id, companiesList),
    [user?.companyId, selectedCompany?.id, companiesList]
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [billingMetaJson, setBillingMetaJson] = useState<string | null>(null);

  const onFinish = async (values: { subscription_type_code: string }) => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    setDone(null);
    try {
      const res = await createDeferredSubscription({
        company_id: companyId,
        subscription_type_code: values.subscription_type_code.trim() || DEFAULT_CODE,
      });
      setDone(res.subscription_id);
      setBillingMetaJson(null);
      try {
        const sub = await SubscriptionService.getCompanySubscriptionById(companyId, res.subscription_id);
        const meta = sub?.billing_metadata;
        if (meta && typeof meta === 'object' && Object.keys(meta).length > 0) {
          setBillingMetaJson(JSON.stringify(meta, null, 2));
        }
      } catch (e: unknown) {
        console.warn('[PartnerDeferredEnrollment] Subscription detail fetch failed after enroll', e);
      }
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not create deferred subscription.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 560, margin: '40px auto', padding: 24 }}>
      <Card>
        <Title level={3} style={{ marginTop: 0 }}>
          Partner deferred enrollment
        </Title>
        <Paragraph type="secondary">
          Creates a pending company subscription with PARTNER_DEFERRED billing metadata (no Stripe checkout).
          Core billing starts when the deferred balance clears per platform rules.
        </Paragraph>
        {!companyId && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Company required"
            description="Select your workspace company in Control Bridge, then return here."
          />
        )}
        {error && <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} />}
        {done && (
          <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 16 }}>
            <Alert
              type="success"
              showIcon
              message="Enrollment recorded"
              description={
                <div>
                  <div>
                    <strong>subscription_id:</strong> {done}
                  </div>
                  <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
                    No Stripe checkout ran. This is a pending PARTNER_DEFERRED arrangement; Core billing
                    starts when the deferred balance clears per platform rules.
                  </Paragraph>
                  <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                    A deferred balance breakdown is not available in the SPA API for this MVP — use this
                    confirmation with ops if you need account-level detail.
                  </Paragraph>
                  {billingMetaJson && (
                    <pre
                      style={{
                        marginTop: 12,
                        marginBottom: 0,
                        padding: 12,
                        background: 'var(--ant-color-fill-quaternary, #f5f5f5)',
                        borderRadius: 6,
                        fontSize: 12,
                        overflow: 'auto',
                        maxHeight: 220,
                      }}
                    >
                      {billingMetaJson}
                    </pre>
                  )}
                </div>
              }
            />
            <Link to="/dashboard">
              <Button type="primary" block>
                Go to dashboard
              </Button>
            </Link>
          </Space>
        )}
        <Form
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ subscription_type_code: DEFAULT_CODE }}
          disabled={!companyId || !!done}
        >
          <Form.Item
            name="subscription_type_code"
            label="Subscription type code"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder={DEFAULT_CODE} autoComplete="off" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              Enroll (deferred)
            </Button>
          </Form.Item>
        </Form>
        <Button type="link" onClick={() => navigate(-1)} style={{ paddingLeft: 0 }}>
          Back
        </Button>
      </Card>
    </div>
  );
};
